import { difference, filter, intersection, isEmpty, map, mapValues, orderBy, some } from 'es-toolkit/compat';

import { Orchestration } from 'api/experimentResults';
import { FlowClassName, ResultsMode } from 'interfaces/experimentResults';
import { Study } from 'interfaces/study';
import { useOrchestrations } from 'utils/queryHooks/useOrchestrations';
import { useJobsByOrchestrationIds } from 'utils/queryHooks/jobs/useJobs';
import { usePermissions } from 'utils/usePermissions';
import { Permission } from 'interfaces/permissionOption';
import { useWorkflowExportSourceUrlState } from 'services/export/useResultsExportUrlState';
import {
  getNoWorkflowResultsNote,
  getWorkflowSourceAvailability,
  hasWorkflowResults,
  resolveWorkflowResultsSource,
  toWorkflowResultsModeParams,
  WorkflowExportKind,
  WorkflowResultsSourceSelection,
  workflowResultsCheckingMessage,
  WorkflowSourceAvailability,
} from 'services/export/workflowExportSource';
import { useCasesParams } from 'utils/useCasesParams';

// Owns the results source of one Workflow export dialog. The user's choice lives in the dialog's URL state; until
// there is one, the source is the Workflow tab's run, else Approved results when there are any, else Published.
// Choosing a run source selects the latest run until the user picks others; any number of runs may be chosen.
// Run ids in the URL that are not among the study's runs are dropped and reported in `staleRunIds`.
// Users who may not view unpublished results are always on Published.
// `availability` is the whole dialog's verdict: still checking which results exist (nothing may be chosen or
// exported yet), nothing to export at all, or the selected source's own availability.
export const useWorkflowResultsSource = (kind: WorkflowExportKind, study?: Study) => {
  const { hasPermission } = usePermissions();
  const canViewUnpublishedResults = hasPermission(Permission.ViewUnpublishedResults);
  const { casesParams } = useCasesParams();
  const urlState = useWorkflowExportSourceUrlState();

  const { data, isInitialLoading, isError, error } = useOrchestrations({
    studyId: study?.id ?? '',
    resultsMode: ResultsMode.Manual,
    enabled: canViewUnpublishedResults,
  });
  const allRuns: Orchestration[] = data?.orchestrations ?? [];
  const runs = orderBy(
    kind === 'features'
      ? filter(allRuns, (run) => some(run.slidesData, { flowClassName: FlowClassName.CalculateFeatures }))
      : allRuns,
    ['createdAtMinimum'],
    ['desc']
  );

  const knownRunIds = map(runs, 'orchestrationId');
  const { jobsByOrchestrationId, isLoading: isLoadingRunNames } = useJobsByOrchestrationIds({
    studyId: study?.id ?? '',
    orchestrationIds: knownRunIds,
    enabled: canViewUnpublishedResults && !isEmpty(knownRunIds),
  });
  const runNames = mapValues(jobsByOrchestrationId, (job) => job?.name);
  const runsAreKnown = !isInitialLoading && !isError;
  const staleRunIds = runsAreKnown && urlState.runIds ? difference(urlState.runIds, knownRunIds) : [];
  const chosen: WorkflowResultsSourceSelection | undefined = urlState.source
    ? {
        source: urlState.source,
        runIds: runsAreKnown && urlState.runIds ? intersection(urlState.runIds, knownRunIds) : urlState.runIds,
      }
    : undefined;
  const selection = study
    ? resolveWorkflowResultsSource({
        kind,
        study,
        canViewUnpublishedResults,
        tabMode: casesParams?.resultsMode,
        tabRunId: casesParams?.filters?.orchestrationId,
        chosen,
        runCount: runs.length,
        latestRunId: runs[0]?.orchestrationId,
        isLoadingRuns: isInitialLoading,
      })
    : ({ source: 'published' } as WorkflowResultsSourceSelection);

  const hasNoResults =
    Boolean(study?.allowExport) &&
    canViewUnpublishedResults &&
    !isInitialLoading &&
    !isError &&
    !hasWorkflowResults({ kind, study: study as Study, runCount: runs.length, canViewUnpublishedResults });
  const availability: WorkflowSourceAvailability = !study?.allowExport
    ? { disabled: true, note: 'Exports are disabled for this study.' }
    : isInitialLoading
    ? { disabled: true, note: workflowResultsCheckingMessage }
    : hasNoResults
    ? { disabled: true, note: getNoWorkflowResultsNote(kind) }
    : getWorkflowSourceAvailability({
        kind,
        selection,
        study,
        runCount: runs.length,
        canViewUnpublishedResults,
      });

  return {
    canViewUnpublishedResults,
    selection,
    select: urlState.select,
    clear: urlState.clear,
    runs,
    staleRunIds,
    isLoadingRuns: isInitialLoading,
    // The run options also wait for the job names, so a run is never listed by its id while they load.
    isLoadingRunOptions: isInitialLoading || isLoadingRunNames,
    runNames,
    hasRunsError: isError,
    runsError: error,
    hasNoResults,
    availability,
    modeParams: toWorkflowResultsModeParams(selection, canViewUnpublishedResults),
  };
};
